import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "urql";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Controller, FormProvider, useForm } from "react-hook-form";
import Page from "../../Page";
import urls from "../../../routes";
import styles from "./Webhook.module.scss";
import Card from "../../../ui/Card/Card";
import TextInput from "../../../ui/TextInput/TextInput";
import SettingsSection from "../../Settings/components/SettingsSection";
import Badge from "../../../ui/Badge/Badge";
import { EmbeddedEditableTable } from "../../Settings/Generation/components/EditableTable/EditableTable";
import EditableCell from "../../Settings/Generation/components/EditableCell/EditableCell";
import OptionsCell from "../../Settings/Generation/components/OptionsCell/OptionsCell";
import Header from "../../Settings/components/Header/Header";
import { getFragmentData, graphql } from "../../../__generated__/gql";
import { WebhookConnectionFragment as WebhookConnectionFragmentType } from "../../../__generated__/gql/graphql";
import Button from "../../../ui/Button/Button";
import { useEffect } from "react";
import { Webhook as WebhookLogo } from "../../../ui/LogoIcon/LogoIcon";
import Feedback from "../../../ui/Feedback/Feedback";

const WebhookConnectionFragment = graphql(/* GraphQL */ `
  fragment WebhookConnection on WebhookConnection {
    id
    url
    connected
    headers {
      key
      value
    }
  }
`);

const WebhookConnectionQuery = graphql(/* GraphQL */ `
  query webhookConnection {
    webhookConnection {
      ...WebhookConnection
    }
  }
`);

const UpdateWebhookConnection = graphql(/* GraphQL */ `
  mutation updateWebhookConnection($input: UpdateWebhookConnectionInput!) {
    updateWebhookConnection(input: $input) {
      ...WebhookConnection
    }
  }
`);

const DeleteWebhookConnection = graphql(/* GraphQL */ `
  mutation deleteWebhookConnection {
    deleteWebhookConnection {
      ...WebhookConnection
    }
  }
`);

type CustomHeader = {
  key: string;
  value: string;
};

const columnHelper = createColumnHelper<CustomHeader>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<CustomHeader, any>[] = [
  columnHelper.accessor("key", {
    header: "Key",
    cell: EditableCell,
    meta: {
      type: "text",
    },
  }),
  columnHelper.accessor("value", {
    header: "Value",
    cell: EditableCell,
    meta: {
      type: "text",
    },
  }),
  columnHelper.display({
    id: "Options",
    size: 72,
    meta: {
      sticky: true,
    },
    cell: OptionsCell,
  }),
];

const schema = z.object({
  url: z.string().refine(
    (value) => {
      try {
        new URL(value);

        return true;
      } catch {
        return false;
      }
    },
    {
      message:
        "You must enter your a valid URL. Make sure to include http:// or https://",
    },
  ),
  headers: z.array(
    z.object({
      key: z.string().optional(),
      value: z.string().optional(),
    }),
  ),
});

type WebhookFormData = z.infer<typeof schema>;

function WebhookForm({
  webhookConnection,
  onSubmit,
}: {
  webhookConnection?: WebhookConnectionFragmentType | null;
  onSubmit: (data: WebhookFormData) => Promise<void>;
}) {
  const [, deleteWebhookConnection] = useMutation(DeleteWebhookConnection);

  const methods = useForm<WebhookFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      url: webhookConnection?.url ?? undefined,
      headers:
        webhookConnection?.headers?.map((header) => ({
          key: header.key ?? undefined,
          value: header.value ?? undefined,
        })) ?? [],
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
  } = methods;

  useEffect(() => {
    reset({
      url: webhookConnection?.url ?? "",
      headers:
        webhookConnection?.headers?.map((header) => ({
          key: header.key ?? "",
          value: header.value ?? "",
        })) ?? [],
    });
  }, [webhookConnection, reset]);

  return (
    <FormProvider {...methods}>
      <div className={styles.container}>
        <Card className={styles.card}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Header
              title="Webhook"
              titleElement={
                webhookConnection?.connected ? (
                  <Badge color="success">Connected</Badge>
                ) : (
                  <Badge color="default">Not Connected</Badge>
                )
              }
              description={
                <>
                  Setup publishing to a custom endpoint. For help, including the
                  shape of the response data, you can check out our{" "}
                  <a
                    href="https://docs.macawhq.com/en/articles/9929052-setting-up-webhook-integration"
                    target="_blank"
                    rel="noreferrer"
                  >
                    webhook setup guide.
                  </a>
                </>
              }
              saveButtonText={webhookConnection?.connected ? "Save" : "Connect"}
              isSubmitting={isSubmitting}
              isDirty={isDirty}
              isDisabled={!isValid}
              onCancel={() => reset()}
            />

            {webhookConnection?.connected && webhookConnection?.url && (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  marginBottom: 20,
                  paddingTop: 24,
                  borderTop: "1px solid var(--color-gray-200)",
                }}
              >
                <>
                  <Badge size="large" variant="badge" showDot={false}>
                    <div
                      style={{ display: "flex", gap: 6, alignItems: "center" }}
                    >
                      <WebhookLogo size="medium" />
                      <span
                        style={{
                          fontWeight: 600,
                          color: "var(--color-gray-800)",
                        }}
                      >
                        {webhookConnection?.url
                          ?.replace("https://", "")
                          .replace("http://", "")}
                      </span>
                    </div>
                  </Badge>
                  <Button
                    variant="link"
                    onClick={async () => {
                      await deleteWebhookConnection({});
                    }}
                  >
                    Disconnect
                  </Button>
                </>
              </div>
            )}
            <SettingsSection
              label="Webhook URL"
              description="The URL you want Macaw to send published articles to."
            >
              <div className={styles.inputRow}>
                <Controller
                  control={control}
                  name="url"
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <TextInput
                        {...field}
                        className={styles.input}
                        placeholder="https://www.website.com/webhook"
                      />

                      {error?.message && (
                        <Feedback className={styles.feedback}>
                          {error.message}
                        </Feedback>
                      )}
                    </>
                  )}
                />
              </div>
            </SettingsSection>
            <SettingsSection
              label="Custom headers"
              description="Any custom headers you want to send with the webhook."
            >
              <EmbeddedEditableTable
                className={styles.table}
                control={control}
                propertyName="headers"
                columns={columns}
                resourceName={{
                  plural: "custom headers",
                  singular: "header",
                }}
                defaultValue={{
                  key: "",
                  value: "",
                }}
                maxRows={5}
              />
            </SettingsSection>
          </form>
        </Card>
      </div>
    </FormProvider>
  );
}

function Webhook() {
  const [webhookConnectionResponse, refetch] = useQuery({
    query: WebhookConnectionQuery,
    requestPolicy: "cache-and-network",
  });

  const [, setupWebhookConnection] = useMutation(UpdateWebhookConnection);

  const webhookConnection = getFragmentData(
    WebhookConnectionFragment,
    webhookConnectionResponse.data?.webhookConnection,
  );

  const onSubmit = async (data: WebhookFormData) => {
    await setupWebhookConnection({
      input: {
        url: data.url,
        headers: data.headers,
      },
    });

    if (!webhookConnection) {
      refetch();
    }
  };

  return (
    <Page
      loading={webhookConnectionResponse.fetching && !webhookConnection}
      title="Webhook"
      documentTitle="Webhook setup"
      breadcrumbs={[
        { url: urls.publishing.connections.root, name: "Publishing" },
      ]}
    >
      <WebhookForm webhookConnection={webhookConnection} onSubmit={onSubmit} />
    </Page>
  );
}

export default Webhook;
